import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { MakeModel } from '@app/_models/makeModel';
import { StateCity } from '@app/_models/stateCity';
import { PlanDetails } from '@app/_models/planDetails';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { map, shareReplay, take } from 'rxjs/operators';
import { PrintInstruction } from '@app/_models/printInstruction';
import { DealerModelConfig } from '@app/_models/dealerModelConfig';
import { PinCodeInfo } from '@app/_models/pincodeInfo';
import { RsaProviderInfo } from '@app/_models/rsaProviderInfo';
import { PlanDealerAssignmentSummary } from '@app/_models/plan-dealer-summary';

@Injectable({
  providedIn: 'root'
})
export class ConfigDataService {

  FinanceCompanyList$ = this.getFinanceCompanyList()
  .pipe(
    shareReplay(1)
  );

  PincodeInfo$ = this.getPincodeInfo()
  .pipe(
    shareReplay(1)
  );
   MakeModel$ = this.getMakeModel()
   .pipe(
     shareReplay(1)
   );

   StateCity$ = this.getStateCity()
   .pipe(
     shareReplay(1)
   );
   PlanDetails$ = this.getPlanDetails()
   .pipe(
     shareReplay(1)
   );

   PrintInstruction$ = this.getPrintInstructionList()
   .pipe(
     shareReplay(1)
   );
   RSAEvFeatures$ = this.getRSAEvFeatures()
   .pipe(
     shareReplay(1)
   );
   
   PolicyCompanies$ = this.getPolicyCompanies()
   .pipe(
     shareReplay(1)
   );
  // MakeModel$ = this.MakeModels$.subscribe();

  constructor(private http: HttpClient) { }

  getInvoiceId() {
    return this.http.get<Number>(`${environment.apiUrl}/counter/InvoiceId`);
  }

  getTodaysDate(): Observable<Date>{
    return this.http.get<Date>(`${environment.apiUrl}/counter/Date`)
  }

  getGST(): number{
    return .18;
  }
  getMakeModel(): Observable<MakeModel[]>{
     return this.http.get<MakeModel[]>(`${environment.apiUrl}/counter/config/Model`)
  }

  getStateCity(): Observable<StateCity[]>{
    return this.http.get<StateCity[]>(`${environment.apiUrl}/counter/config/State`);
  }
  getPincodeInfo(): Observable<PinCodeInfo[]>{
    return this.http.get<PinCodeInfo[]>(`${environment.apiUrl}/counter/config/PincodeInfo`);
  }

  getPlanDetails(): Observable<PlanDetails[]>{
    return this.http.get<PlanDetails[]>(`${environment.apiUrl}/counter/config/Plan`);
  }

  getFinanceCompanyList(): Observable<string[]>{
    return this.http.get<string[]>(`${environment.apiUrl}/counter/config/Finance`);
  }

  getPrintInstructionList(): Observable<PrintInstruction[]>{
    return this.http.get<PrintInstruction[]>(`${environment.apiUrl}/counter/config/PrintInstruction`);
  }
  getRSAEvFeatures(): Observable<any[]>{
    return this.http.get<any[]>(`${environment.apiUrl}/counter/config/RSAEvFeatures`);
  }
  
  getPolicyCompanies(): Observable<string[]>{
    return this.http.get<string[]>(`${environment.apiUrl}/counter/config/PolicyCompanies`);
  }
  getPlanDealerAssignments(): Observable<PlanDealerAssignmentSummary[]> {
    return this.http.get<PlanDealerAssignmentSummary[]>(`${environment.apiUrl}/counter/plan/dealerAssignments`);
  }

  /**
   * Get cached plan-dealer assignments for fast loading.
   * This uses the DealerPlanMapping collection for optimized retrieval.
   */
  getCachedPlanDealerAssignments(): Observable<PlanDealerAssignmentSummary[]> {
    return this.http.get<PlanDealerAssignmentSummary[]>(`${environment.apiUrl}/counter/plan/dealerAssignments/cached`);
  }

  /**
   * Force sync/rebuild the plan-dealer mapping cache.
   * Call this after mass changes or when cache needs refreshing.
   */
  syncPlanDealerMappings(): Observable<{ synced: number; message: string }> {
    return this.http.post<{ synced: number; message: string }>(`${environment.apiUrl}/counter/plan/dealerAssignments/sync`, {});
  }

  deletePlan(planId: string): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/counter/plan/${planId}`);
  }
  getConfig(keyValue: string) {
    return this.http.get(`${environment.apiUrl}/counter/config/${keyValue}`);
  }

  getConfigCounter(configId:any): Observable<any>{
    return this.http.get(`${environment.apiUrl}/counter/getConfigCounter/${configId}`);
  }

  getStringCacheConfig(keyValue: string): Observable<string>{
    return this.http.get<string>(`${environment.apiUrl}/counter/getStringCacheConfig/${keyValue}`);
  }
  resetStringCacheConfig(inputData: any): Observable<string>{
    if (inputData.configType.toUpperCase() == "COUNTER")
    return this.http.post<string>(`${environment.apiUrl}/counter/setConfigCounter/`,inputData);
    else
    {
    if (inputData.configType.toUpperCase() == "ARRAY")
    {
      if(inputData.keyValue != "Plan")
      inputData.ResultValue = JSON.parse(inputData.ResultValue);
    }
    return this.http.post<string>(`${environment.apiUrl}/counter/resetStringCacheConfig/`,inputData);
  }
  }

  getRoles(): Observable<string[]>{
    return of ( ["admin","executive","dealerOwner","manager","employee","rsaOperation","finance","heroEmployee"
  ])
  }
  getSpecialRoles(): Observable<string[]>{
    return of ( ["CanViewDealerBalanceReport","ViewNetAmountGST","CannotViewDealerBalance","CanViewOldCertificate","ExcelUpload","CanRenewAllPolicies","canSeeAge","canSeeNewIndiaLogo","HeroReport",
    "cannotSeeIffcoTokioLogo","CanSeePolicyBalance","Can View Policy Balance Details Report","Can View Package Balance Detail Report","Show Total Package Balance","CanSeeOnlyNewPolicies"
    ,"canSeePolicyCompanyLogo"
    ,"newPolicyPrint"
    ,"538539AddPayment"
    ,"AllowHeroPolicyPDFUpload"
    ,"AllowHeroPolicyPDFUploadAI"
    ,"HeroSuperAdmin"
    ,"HeroManager"
    ,"HeroExecutive"
  ])
  }
  getDealerModelConfig(dealerId: string): Observable<DealerModelConfig[]>{
    return this.http.get<DealerModelConfig[]>(`${environment.apiUrl}/counter/dealerConfig/DealerModelConfig/${dealerId}`);
  }
  getMasterPolicyMaintain(): Observable<any[]>{
    return this.http.get<any[]>(`${environment.apiUrl}/counter/config/MasterPolicyMaintain`);
  }
  getSeriesMaintain(): Observable<any[]>{
    return this.http.get<any[]>(`${environment.apiUrl}/counter/config/SeriesMaintain`);
  }
  getSetupMaintain(): Observable<any[]>{
    return this.http.get<any[]>(`${environment.apiUrl}/counter/config/SetupMaintain`);
  }
  SeriesMaintain$ = this.getSeriesMaintain()
  .pipe(
    shareReplay(1)
  );
  SetupMaintain$ = this.getSetupMaintain()
  .pipe(
    shareReplay(1)
  );
  MasterPolicyMaintain$ = this.getMasterPolicyMaintain()
  .pipe(
    shareReplay(1)
  );
  addRsaProvider(provider: any) {
    return this.http.post(`${environment.apiUrl}/rsaprovider/config/AddRsaProvider`, provider);
  }
  getAllRsaProviders(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/rsaprovider/config/GetAllRsaProviders`);
  }

  getRsaProviderByName(providerName: string): Observable<RsaProviderInfo[]> {
    return this.http.get<RsaProviderInfo[]>(`${environment.apiUrl}/rsaprovider/config/GetRsaProviderByName`, {
      params: { providerName }
    });
  }

  getRsaProviderByCity(city: string): Observable<RsaProviderInfo[]> {
    return this.http.get<RsaProviderInfo[]>(`${environment.apiUrl}/rsaprovider/config/GetRsaProviderByCity`, {
      params: { city }
    });
  }

  getRsaProviderByState(state: string): Observable<RsaProviderInfo[]> {
    return this.http.get<RsaProviderInfo[]>(`${environment.apiUrl}/rsaprovider/config/GetRsaProviderByState`, {
      params: { state }
    });
  }

  getRsaProviderByLocation(latitude: string, longitude: string): Observable<RsaProviderInfo[]> {
    return this.http.get<RsaProviderInfo[]>(`${environment.apiUrl}/rsaprovider/config/GetRsaProviderByLocation`, {
      params: { latitude, longitude }
    });
  }
}
